import {useMemo, useState} from 'react';
import type {ReactNode} from 'react';
import classNames from 'classnames';

import {useIntl} from 'react-intl';
import isEmpty from '@tinkoff/utils/is/empty';

import type {FileInfo} from '../../../utils/attachments';

import {fileSizeToString, getFileType} from '../../../utils/attachments';

import {AttachmentCard, AttachmentCardActions, AttachmentCardText} from '../../../atoms/attachment';
import {AttachmentThumbnail} from '../../../molecules/attachment-thumbnail';

import styles from './file-attachment.module.css';

type Props = {
    fileInfo: FileInfo;
    label?: string;
    getThumbnailUrl: (id: string) => string | undefined;
    action?: ReactNode;
    className?: string;
    onClick?: (fileInfo: FileInfo) => unknown;
};

export const FileAttachment = ({fileInfo, label: labelProp, className, getThumbnailUrl, action, onClick}: Props) => {
    const {formatMessage} = useIntl();

    const [isHovered, setHovered] = useState(false);
    const [isFocused, setFocused] = useState(false);

    const handleClick = () => onClick?.(fileInfo);
    const setHoveredOn = () => setHovered(true);
    const setHoveredOff = () => setHovered(false);
    const setFocusedOn = () => setFocused(true);
    const setFocusedOff = () => setFocused(false);

    const fileType = getFileType(fileInfo.extension);
    const isInteractive = !isEmpty(onClick);
    const showLabel = isInteractive && (isHovered || isFocused);

    const label = useMemo(
        () =>
            labelProp ??
      formatMessage({
          id: 'file_attachment.open_file',
          defaultMessage: 'Open file',
      }),
        [formatMessage, labelProp],
    );

    const content = (
        <>
            <AttachmentThumbnail
                src={getThumbnailUrl(fileInfo.id)}
                fileName={fileInfo.name}
                fileType={fileType}
                fileExtension={fileInfo.extension}
            />
            <AttachmentCardText
                textPrimary={fileInfo.name}
                textSecondary={showLabel ? label : `${fileSizeToString(fileInfo.size)}`}
            />
            <AttachmentCardActions className={styles.actions}>{action}</AttachmentCardActions>
        </>
    );

    return isInteractive ? (
        <AttachmentCard
            as='button'
            type='button'
            aria-label={label}
            onClick={handleClick}
            className={classNames(styles.root, styles.interactive, className)}
            onMouseEnter={setHoveredOn}
            onMouseLeave={setHoveredOff}
            onFocus={setFocusedOn}
            onBlur={setFocusedOff}
        >
            {content}
        </AttachmentCard>
    ) : (
        <AttachmentCard className={classNames(styles.root, className)}>{content}</AttachmentCard>
    );
};
